import { useTranslation } from "@roastery/i18n";
import { ConfirmDialogProvider } from "@roastery/ui";
import { UiLabels } from "../components/ui-labels";
import { Outlet, createRootRoute } from "@tanstack/react-router";
import { FullScreenMessage } from "../components/full-screen-message";
import { BrandingTheme } from "../components/roastery-mark";
import { usePublicBranding } from "../lib/branding";

export const Route = createRootRoute({
  component: PortalRoot,
  notFoundComponent: NotFound,
});

/**
 * Everything the portal has in common, and nothing that needs a token (US-24.4).
 *
 * <p>Until US-24.4 this file also held the sign-in gate, which meant the whole portal was behind it —
 * fine while every screen belonged to a wholesale buyer. The page under the QR on a bag does not: it
 * is read by somebody holding a bag in a shop, who has no account and never will. So the gate moved
 * one level down, into the pathless `_authed` layout, and what is left here is what both audiences
 * share: the roastery's colour and its name in the browser tab, from the slug in the address.
 *
 * <p>The branding call is the same one the login screen has made since US-10.5. It needs no token,
 * which is what makes it usable by both.
 */
function PortalRoot() {
  const branding = usePublicBranding();

  return (
    // One confirmation dialog for the whole portal (US-23.6): a café deleting a standing order is
    // asked in the roastery's own screen, not in a browser box it cannot read in its own language.
    <UiLabels>
      <ConfirmDialogProvider>
        <BrandingTheme
          name={branding.data?.name}
          logoUrl={branding.data?.logoUrl}
          primaryColor={branding.data?.primaryColor}
        />
        <Outlet />
      </ConfirmDialogProvider>
    </UiLabels>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return <FullScreenMessage title={t("portal.notFound")} testId="portal-not-found" />;
}
